import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { useFloating, autoUpdate, offset, flip, shift, FloatingPortal } from '@floating-ui/react';
import { AlertIcon, InfoIcon, EditIcon, ArrowRightIcon } from '../../ui/icons';
import { useTranslation } from '../../translations';
import SupportRequestModal from './SupportRequestModal';

/**
 * H.2 — stały przycisk „?” w prawym dolnym rogu panelu dla KAŻDEGO zalogowanego (także pracownika
 * i konta demo). Menu: zgłoś błąd, zadaj pytanie, zaproponuj zmianę, a na stronach z mini-przewodnikiem
 * (utils/pageTours.js) także „Przewodnik po tej stronie”.
 *
 * Przycisk jest renderowany PORTALEM do `body`, a nie w drzewie strony: element `position: fixed`
 * wewnątrz przodka z `transform`/`filter` (animacje wejścia stron, okna) przestaje trzymać się okna
 * przeglądarki (patrz CLAUDE.md, „Animacja wejścia strony bez transform”). Dzięki portalowi zostaje
 * w rogu przy przewijaniu każdej strony.
 *
 * Toasty i podobne powiadomienia stoją w tym samym rogu, więc przycisk ogłasza swoje miejsce
 * zmienną CSS `--help-fab-space` na `:root`; warstwa toastów (components/ui/Toast.js), dolny odstęp
 * treści strony (`MainContent` w App.js) i powiadomienie „Cofnij” w Magazynie doliczają ją do
 * odstępu od dołu. Bez przycisku (strony publiczne) zmiennej nie ma i nic się nie przesuwa.
 */

// Rozmiar i odstęp przycisku oraz miejsce, które rezerwuje nad sobą (przycisk + odstęp od krawędzi
// + przerwa między przyciskiem a toastem). Zmiana jednego wymaga zmiany drugiego.
const ROZMIAR = 48;
const MIEJSCE = '72px';

const Fab = styled.button`
  position: fixed;
  right: calc(var(--sp-4) + var(--safe-right, 0px));
  bottom: calc(var(--sp-4) + var(--safe-bottom, 0px));
  z-index: var(--z-sticky);
  width: ${ROZMIAR}px;
  height: ${ROZMIAR}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: var(--primary);
  color: #fff;
  font-family: inherit;
  font-size: var(--fs-xl);
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 6px 18px var(--shadow-color);
  transition: box-shadow var(--dur) var(--ease), opacity var(--dur) var(--ease);

  &:hover { box-shadow: 0 8px 24px var(--shadow-color); opacity: 0.92; }
  &:focus-visible { outline: 3px solid var(--primary); outline-offset: 3px; }

  @media print { display: none; }
`;

const Menu = styled.div`
  z-index: var(--z-popover);
  min-width: 240px;
  max-width: min(320px, calc(100vw - 24px));
  padding: var(--sp-1);
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: var(--r-md);
  box-shadow: var(--shadow-lg);
  outline: none;
`;

// Pozycje menu nie są w kolejności Tab (`tabIndex: -1`): menu to jeden przystanek, a po pozycjach
// chodzi się strzałkami (wzorzec WAI-ARIA dla przycisku z menu).
const Item = styled.button.attrs({ type: 'button', role: 'menuitem', tabIndex: -1 })`
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  width: 100%;
  min-height: 44px;
  padding: var(--sp-2) var(--sp-3);
  border: none;
  border-radius: var(--r-sm);
  background: transparent;
  color: var(--text);
  font: inherit;
  font-size: var(--fs-sm);
  font-weight: 600;
  text-align: left;
  cursor: pointer;

  svg { width: 18px; height: 18px; flex-shrink: 0; color: var(--primary); }
  &:hover, &:focus-visible { background: var(--hover-bg); outline: none; }
`;

const Separator = styled.div.attrs({ role: 'separator' })`
  height: 1px;
  margin: var(--sp-1) var(--sp-2);
  background: var(--input-border);
`;

export default function HelpButton({ user, pageTourAvailable, onOpenPageTour }) {
  const { t } = useTranslation();
  const menuId = useId();
  const [menuOtwarte, setMenuOtwarte] = useState(false);
  const [kategoria, setKategoria] = useState(null); // otwarte okno zgłoszenia z tą kategorią
  const menuRef = useRef(null);

  const { refs, floatingStyles } = useFloating({
    open: menuOtwarte,
    placement: 'top-end',
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip({ padding: 8 }), shift({ padding: 8 })],
  });

  // Miejsce w rogu dla toastów i końca strony (patrz nagłówek pliku).
  useEffect(() => {
    const korzen = document.documentElement;
    korzen.style.setProperty('--help-fab-space', MIEJSCE);
    return () => korzen.style.removeProperty('--help-fab-space');
  }, []);

  const zamknijMenu = useCallback((wrocFokus = true) => {
    setMenuOtwarte(false);
    if (wrocFokus && refs.domReference.current) refs.domReference.current.focus();
  }, [refs]);

  // Fokus na pierwszej pozycji w chwili pojawienia się menu (portal montuje je o klatkę później,
  // więc nie w efekcie na `menuOtwarte`; ten sam wzorzec co components/ui/SelectDropdown.js).
  const { setFloating } = refs;
  const podepnijMenu = useCallback((el) => {
    setFloating(el);
    menuRef.current = el;
    if (el) {
      const pierwsza = el.querySelector('[role="menuitem"]');
      if (pierwsza) pierwsza.focus({ preventScroll: true });
    }
  }, [setFloating]);

  // Klik poza przyciskiem i menu zamyka menu bez przenoszenia fokusu.
  useEffect(() => {
    if (!menuOtwarte) return undefined;
    const naNacisniecie = (e) => {
      const cel = e.target;
      if (refs.domReference.current?.contains(cel) || menuRef.current?.contains(cel)) return;
      zamknijMenu(false);
    };
    document.addEventListener('mousedown', naNacisniecie);
    document.addEventListener('touchstart', naNacisniecie);
    return () => {
      document.removeEventListener('mousedown', naNacisniecie);
      document.removeEventListener('touchstart', naNacisniecie);
    };
  }, [menuOtwarte, refs, zamknijMenu]);

  const naKlawiszMenu = (e) => {
    const pozycje = Array.from(menuRef.current ? menuRef.current.querySelectorAll('[role="menuitem"]') : []);
    const i = pozycje.indexOf(document.activeElement);
    const idz = (n) => { e.preventDefault(); if (pozycje.length) pozycje[(n + pozycje.length) % pozycje.length].focus(); };
    if (e.key === 'ArrowDown') idz(i + 1);
    else if (e.key === 'ArrowUp') idz(i - 1);
    else if (e.key === 'Home') idz(0);
    else if (e.key === 'End') idz(pozycje.length - 1);
    // Tab i Escape zamykają menu i oddają fokus przyciskowi. Menu siedzi w portalu na końcu `body`,
    // więc domyślny Tab z pozycji wyrzucałby fokus poza stronę albo na jej początek.
    else if (e.key === 'Escape' || e.key === 'Tab') { e.preventDefault(); zamknijMenu(true); }
  };

  const naKlawiszPrzycisku = (e) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); setMenuOtwarte(true); }
  };

  const otworzZgloszenie = (k) => {
    setMenuOtwarte(false);
    setKategoria(k);
  };

  const otworzPrzewodnik = () => {
    setMenuOtwarte(false);
    if (onOpenPageTour) onOpenPageTour();
  };

  const przycisk = (
    <Fab
      ref={refs.setReference}
      type="button"
      aria-label={t('help.button.aria')}
      title={t('help.button.aria')}
      aria-haspopup="menu"
      aria-expanded={menuOtwarte}
      aria-controls={menuOtwarte ? menuId : undefined}
      onClick={() => setMenuOtwarte((v) => !v)}
      onKeyDown={naKlawiszPrzycisku}
      data-cy="help-button"
    >
      ?
    </Fab>
  );

  return (
    <>
      {typeof document !== 'undefined' ? ReactDOM.createPortal(przycisk, document.body) : null}
      {menuOtwarte && (
        <FloatingPortal>
          <Menu
            ref={podepnijMenu}
            id={menuId}
            role="menu"
            aria-label={t('help.menu.label')}
            tabIndex={-1}
            style={floatingStyles}
            onKeyDown={naKlawiszMenu}
            data-cy="help-menu"
          >
            <Item onClick={() => otworzZgloszenie('bug')} data-cy="help-menu-bug">
              <AlertIcon />{t('help.menu.bug')}
            </Item>
            <Item onClick={() => otworzZgloszenie('question')} data-cy="help-menu-question">
              <InfoIcon />{t('help.menu.question')}
            </Item>
            <Item onClick={() => otworzZgloszenie('idea')} data-cy="help-menu-idea">
              <EditIcon />{t('help.menu.idea')}
            </Item>
            {pageTourAvailable ? (
              <>
                <Separator />
                <Item onClick={otworzPrzewodnik} data-cy="help-menu-tour">
                  <ArrowRightIcon />{t('help.menu.tour')}
                </Item>
              </>
            ) : null}
          </Menu>
        </FloatingPortal>
      )}
      <SupportRequestModal
        open={kategoria !== null}
        kategoriaStartowa={kategoria}
        onClose={() => {
          setKategoria(null);
          if (refs.domReference.current) refs.domReference.current.focus();
        }}
        user={user}
      />
    </>
  );
}
